import { useState, useEffect } from 'react';

interface Props {
  darkMode: boolean;
  onClose: () => void;
}

const CHANGELOG = [
  {
    version: 'v2.4',
    date: 'Latest',
    badge: 'New',
    badgeColor: 'bg-teal-500',
    items: [
      {
        icon: 'ri-file-pdf-line',
        color: 'text-rose-500',
        bg: 'bg-rose-50',
        darkBg: 'bg-rose-900/20',
        title: 'PDF Export',
        desc: 'Export your full construction documents as a professionally formatted PDF with one click.',
      },
      {
        icon: 'ri-printer-line',
        color: 'text-indigo-500',
        bg: 'bg-indigo-50',
        darkBg: 'bg-indigo-900/20',
        title: 'Print Documents',
        desc: 'Open a clean print-optimized layout of your construction docs directly in the browser print dialog.',
      },
      {
        icon: 'ri-share-line',
        color: 'text-cyan-500',
        bg: 'bg-cyan-50',
        darkBg: 'bg-cyan-900/20',
        title: 'Share & Copy Link',
        desc: 'Generate a shareable URL with your entire project config encoded — send it to anyone instantly.',
      },
    ],
  },
  {
    version: 'v2.3',
    date: '2 weeks ago',
    badge: 'Update',
    badgeColor: 'bg-amber-500',
    items: [
      {
        icon: 'ri-confetti-line',
        color: 'text-yellow-500',
        bg: 'bg-yellow-50',
        darkBg: 'bg-yellow-900/20',
        title: 'Confetti Celebration',
        desc: 'Finish all 6 tutorial chapters and enjoy a burst of confetti with a celebration banner!',
      },
      {
        icon: 'ri-bookmark-line',
        color: 'text-teal-500',
        bg: 'bg-teal-50',
        darkBg: 'bg-teal-900/20',
        title: 'Chapter Bookmarks',
        desc: 'Bookmark specific moments in tutorial chapters for quick reference later.',
      },
      {
        icon: 'ri-sticky-note-line',
        color: 'text-amber-500',
        bg: 'bg-amber-50',
        darkBg: 'bg-amber-900/20',
        title: 'Notes Panel',
        desc: 'Jot down notes per tutorial chapter and export them all as a .txt file.',
      },
    ],
  },
  {
    version: 'v2.2',
    date: '1 month ago',
    badge: 'Update',
    badgeColor: 'bg-violet-500',
    items: [
      {
        icon: 'ri-file-copy-line',
        color: 'text-violet-500',
        bg: 'bg-violet-50',
        darkBg: 'bg-violet-900/20',
        title: 'Duplicate Project',
        desc: 'Clone any saved project as a new slot with a single click — perfect for experimenting.',
      },
      {
        icon: 'ri-pencil-line',
        color: 'text-green-500',
        bg: 'bg-green-50',
        darkBg: 'bg-green-900/20',
        title: 'Rename Projects',
        desc: 'Edit project names inline directly from the My Projects panel.',
      },
      {
        icon: 'ri-search-line',
        color: 'text-blue-500',
        bg: 'bg-blue-50',
        darkBg: 'bg-blue-900/20',
        title: 'Chapter Search',
        desc: 'Filter tutorial chapters by title, tag, or topic with an instant search bar.',
      },
    ],
  },
];

const SEEN_KEY = 'masterArchitectChangelogSeen';

export default function WhatsNewModal({ darkMode, onClose }: Props) {
  const [activeVersion, setActiveVersion] = useState(0);

  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';

  const handleClose = () => {
    localStorage.setItem(SEEN_KEY, CHANGELOG[0].version);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${bg} rounded-2xl shadow-2xl w-full max-w-2xl border ${border} overflow-hidden flex flex-col`} style={{ maxHeight: '88vh' }}>
        {/* Header */}
        <div className={`px-6 py-5 border-b ${border} flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <i className="ri-sparkling-line text-xl text-white"></i>
            </div>
            <div>
              <h2 className={`text-lg font-bold ${textPrimary}`}>What's New</h2>
              <p className={`text-xs ${textSecondary}`}>Master Architect — Recent Updates</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Version tabs */}
        <div className={`flex gap-2 px-6 pt-4 pb-0 flex-shrink-0`}>
          {CHANGELOG.map((log, idx) => (
            <button
              key={log.version}
              onClick={() => setActiveVersion(idx)}
              className={`px-4 py-2 rounded-t-xl text-xs font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeVersion === idx
                  ? `${textPrimary} border-teal-500`
                  : `${textSecondary} border-transparent hover:border-gray-300`
              }`}
            >
              <span className={`inline-block px-2 py-0.5 rounded-full text-white text-xs mr-2 ${log.badgeColor}`}>
                {log.badge}
              </span>
              {log.version}
              <span className={`ml-2 text-xs ${textSecondary}`}>{log.date}</span>
            </button>
          ))}
        </div>

        <div className={`h-px ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} mx-6 flex-shrink-0`}></div>

        {/* Feature cards */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {CHANGELOG[activeVersion].items.map((item, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-4 p-4 rounded-xl border ${border} ${cardBg} transition-all hover:shadow-md`}
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${darkMode ? item.darkBg : item.bg}`}>
                <i className={`${item.icon} text-xl ${item.color}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${textPrimary} mb-1`}>{item.title}</p>
                <p className={`text-xs leading-relaxed ${textSecondary}`}>{item.desc}</p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-teal-500 text-base"></i>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${border} flex items-center justify-between flex-shrink-0 ${cardBg}`}>
          <p className={`text-xs ${textSecondary}`}>
            <i className="ri-information-line mr-1"></i>
            Updates are released regularly. Stay tuned!
          </p>
          <button
            onClick={handleClose}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

export { SEEN_KEY };
